<template>
  <v-expansion-panels class="pt-3" v-model="panelIndex">
    <v-expansion-panel class="transparent">
      <v-expansion-panel-header color="primary">
        <v-row>
          <icon class="ml-6 mr-4" color="white" scale="1.5" name="chart-line" />
          <span class="font-weight-bold mt-1 text-truncate white--text"
            >Maneuver comparator
          </span>
          <exo-help-dialog
            :color="'white--text'"
            :margin="'ma-0'"
            title="Maneuver comparator"
          >
            <tooltipEvasiveManeuverComparator />
          </exo-help-dialog>
          <v-btn
            :disabled="filteredComparedManeuversReports.length === 0"
            class="mr-4 white--text"
            color="grey darken-3"
            @click.stop="exportComparedManeuvers()"
            small
            >Export compared maneuvers</v-btn
          >
        </v-row>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-row class="px-3 pt-3">
          <v-col cols="3" v-if="computeChaserReaction === true">
            <v-row>
              <v-checkbox
                v-model="displayChaserReaction"
                :label="`Display Chaser Reaction Maneuvers`"
              ></v-checkbox
              ><exo-help-dialog margin="mt-4" title="Chaser Reaction Maneuver">
                <tooltipChaserReactionDisplayOption />
              </exo-help-dialog>
            </v-row>
          </v-col>
          <v-col cols="4">
            <v-row>
              <v-checkbox
                v-model="displayDistanceToNominal"
                :label="`Display Target Distance To Nominal Trajectory`"
              ></v-checkbox
              ><exo-help-dialog
                margin="mt-4"
                title="Target Distance To Nominal Trajectory"
              >
                <tooltipTargetDistanceToNominalDisplayOption />
              </exo-help-dialog>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="mb-n8">
          <v-col cols="2" class="d-flex justify-left align-center"
            >Only plot data between</v-col
          >
          <v-col cols="4">
            <exoDatetimePicker
              outlined
              class="mt-3"
              dense
              :label="'Start date'"
              v-model="startDate"
              :datePickerProps="datePickerPropsFirstDate"
            />
          </v-col>
          <v-col cols="1" class="d-flex justify-center align-center">
            and
          </v-col>
          <v-col cols="4">
            <exoDatetimePicker
              outlined
              class="mt-3"
              dense
              :label="'End date'"
              v-model="endDate"
              :datePickerProps="datePickerPropsLastDate"
            />
          </v-col>
        </v-row>

        <v-row class="px-3 pt-3">
          <v-col
            v-for="maneuver in filteredComparedManeuversReports"
            :key="maneuver.id"
          >
            <EvasiveManeuverComparatorCard
              v-on:update-panel-indexes="updatePanelIndexes($event)"
              :panelIndexes="panelIndexes"
              :maneuver="maneuver"
              :unmodifiedManeuver="
                comparedManeuversReports.find(
                  (maneuverReport) => maneuverReport.id === maneuver.id
                )
              "
              :referenceChaserToTargetRelativeDistanceResults="
                filteredReferenceChaserToTargetRelativeDistanceResults
              "
              :bestComparedValues="bestComparedValues"
              :relativeDistanceRangesByAxis="rangesByAxis"
              :relativeDistanceToNominalRange="relativeDistanceToNominalRange"
              :computeServiceRecovery="computeServiceRecovery"
              :displayChaserReaction="displayChaserReaction"
              :displayDistanceToNominal="displayDistanceToNominal"
              :startDate="startDate"
              :endDate="endDate"
              v-on:remove-maneuver="removeManeuverFromComparator($event)"
            />
          </v-col> </v-row
      ></v-expansion-panel-content> </v-expansion-panel
  ></v-expansion-panels>
</template>

<script>
import resultsDescription from '../../tools/resultsDescription.json'
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import asterxMixin from './asterx.mixin.js'

import tooltipEvasiveManeuverComparator from '../../components/Tooltips/Defense/tooltipEvasiveManeuverComparator'
import tooltipChaserReactionDisplayOption from '../../components/Tooltips/Defense/tooltipChaserReactionDisplayOption'
import tooltipTargetDistanceToNominalDisplayOption from '../../components/Tooltips/Defense/tooltipTargetDistanceToNominalDisplayOption'

import exoDatetimePicker from '../../components/Forms/dateTimePicker.vue'

import EvasiveManeuverComparatorCard from './EvasiveManeuverComparatorCard'

Vue.use(dashboardComponents)

export default {
  name: 'EvasiveManeuverComparator',

  mixins: [errorMixin, asterxMixin],
  components: {
    EvasiveManeuverComparatorCard,
    tooltipEvasiveManeuverComparator,
    tooltipTargetDistanceToNominalDisplayOption,
    tooltipChaserReactionDisplayOption,
    exoDatetimePicker
  },

  data() {
    return {
      expansion: [0],
      statsCardName: 'statsCard',
      type: 'ReactionManeuverReport',
      displayChaserReaction: false,
      displayDistanceToNominal: false,
      panelIndex: 0,
      panelIndexes: [0, 1],
      startDate: null,
      endDate: null,
      locallySavedParams: ['displayChaserReaction', 'displayDistanceToNominal']
    }
  },

  mounted() {
    this.restoreLocallySavedParameters(this.locallySavedParams)
  },

  props: {
    computeServiceRecovery: {
      type: Boolean,
      default: false
    },
    referenceChaserToTargetRelativeDistanceResults: {
      type: Object
    },
    computeChaserReaction: { type: Boolean, default: false },
    comparedManeuversReports: {
      type: Array
    }
  },

  watch: {
    displayChaserReaction() {
      this.updateLocallySavedParameters(this.locallySavedParams)
    },
    displayDistanceToNominal() {
      this.updateLocallySavedParameters(this.locallySavedParams)
    }
  },

  computed: {
    datePickerPropsFirstDate() {
      const firstdate =
        this.referenceChaserToTargetRelativeDistanceResults
          ?.propagationDates?.[0]
      return firstdate ? { 'show-current': firstdate.substring(0, 10) } : {}
    },
    datePickerPropsLastDate() {
      const lastdate =
        this.referenceChaserToTargetRelativeDistanceResults?.propagationDates?.[
          this.referenceChaserToTargetRelativeDistanceResults?.propagationDates
            ?.length - 1
        ]
      return lastdate ? { 'show-current': lastdate.substring(0, 10) } : {}
    },
    bestComparedValues() {
      let bestTargetReactionDuration,
        bestClosestApproachDistance,
        bestTargetTotalDeltaV,
        bestTargetServiceLossDuration,
        bestCrossingDate
      this.filteredComparedManeuversReports.forEach((m, _) => {
        let evasiveReport = m.targetReactionManeuverReport
        if (
          !isNaN(evasiveReport.targetReactionDuration) &&
          (!bestTargetReactionDuration ||
            evasiveReport.targetReactionDuration > bestTargetReactionDuration)
        ) {
          bestTargetReactionDuration = evasiveReport.targetReactionDuration
        }
        if (
          !isNaN(evasiveReport.closestApproachDistance) &&
          (!bestClosestApproachDistance ||
            evasiveReport.closestApproachDistance > bestClosestApproachDistance)
        ) {
          bestClosestApproachDistance = evasiveReport.closestApproachDistance
        }
        if (
          !isNaN(evasiveReport.targetServiceLossDuration) &&
          (!bestTargetServiceLossDuration ||
            evasiveReport.targetServiceLossDuration <
              bestTargetServiceLossDuration)
        ) {
          bestTargetServiceLossDuration =
            evasiveReport.targetServiceLossDuration
        }
        if (
          !bestCrossingDate ||
          evasiveReport.upwardThresholdCrossingDate <= bestCrossingDate
        ) {
          bestCrossingDate = evasiveReport.upwardThresholdCrossingDate
        }
        if (
          !isNaN(evasiveReport.targetTotalDeltaV) &&
          (!bestTargetTotalDeltaV ||
            evasiveReport.targetTotalDeltaV < bestTargetTotalDeltaV)
        ) {
          bestTargetTotalDeltaV = evasiveReport.targetTotalDeltaV
        }
      })

      return {
        targetReactionDuration: bestTargetReactionDuration,
        closestApproachDistance: bestClosestApproachDistance,
        targetTotalDeltaV: bestTargetTotalDeltaV,
        targetServiceLossDuration: bestTargetServiceLossDuration,
        upwardThresholdCrossingDate: bestCrossingDate
      }
    },

    relativeDistanceFields() {
      return [
        'relativeDistanceEphemerides',
        'relativeDistanceT',
        'relativeDistanceW',
        'relativeDistanceN'
      ]
    },
    rangesByAxis() {
      const ranges = {}
      for (const field of this.relativeDistanceFields) {
        ranges[field] = this.relativeDistanceComparatorRange(
          this.filteredComparedManeuversReports,
          field
        )
      }
      return ranges
    },
    relativeDistanceToNominalRange() {
      let currentRange = null
      const context = 'ExtendedRelativeDistanceResults'
      this.filteredComparedManeuversReports.forEach((maneuver) => {
        const relativeDistances =
          maneuver.evasiveTargetToNominalRelativeDistanceResults.relativeDistanceEphemerides.map(
            (x) =>
              this.getDisplayValueFromPath(
                x,
                ['relativeDistanceEphemerides'],
                context
              )
          )

        const maneuverRange = this.getRange(relativeDistances)
        currentRange = currentRange
          ? [
              Math.min(currentRange[0], maneuverRange[0]),
              Math.max(currentRange[1], maneuverRange[1])
            ]
          : maneuverRange
      })
      return currentRange
    },
    filteredReferenceChaserToTargetRelativeDistanceResults() {
      return this.filterRelativeDistanceResult(
        this.referenceChaserToTargetRelativeDistanceResults,
        this.startDate,
        this.endDate
      )
    },
    filteredComparedManeuversReports() {
      return this.comparedManeuversReports.map((maneuverReports) =>
        this.filterManeuverReport(maneuverReports, this.startDate, this.endDate)
      )
    }
  },

  methods: {
    updatePanelIndexes(newPanelIndexes) {
      this.panelIndexes = newPanelIndexes
    },
    filterRelativeDistanceResult(relativeDistanceResult, startDate, endDate) {
      if (relativeDistanceResult == null) {
        return null
      }
      const timeLimitedData = {}
      //To avoid vue performance problem
      relativeDistanceResult = { ...relativeDistanceResult }

      for (const key in relativeDistanceResult) {
        if (
          relativeDistanceResult[key]?.length ===
          relativeDistanceResult.propagationDates.length
        ) {
          timeLimitedData[key] = relativeDistanceResult[key].filter(
            (data, index) => {
              const dataDate = relativeDistanceResult.propagationDates[index]
              return (
                (startDate == null || dataDate >= startDate) &&
                (endDate == null || dataDate <= endDate)
              )
            }
          )
        }
      }
      return { ...relativeDistanceResult, ...timeLimitedData }
    },
    filterReactionManeuverReport(reactionManeuverReport, startDate, endDate) {
      if (reactionManeuverReport == null) {
        return null
      }
      const filteredRelativeDistanceResults = this.filterRelativeDistanceResult(
        reactionManeuverReport.relativeDistanceResults,
        startDate,
        endDate
      )
      return {
        ...reactionManeuverReport,
        relativeDistanceResults: filteredRelativeDistanceResults
      }
    },
    filterManeuverReport(maneuverReport, startDate, endDate) {
      if (maneuverReport == null) {
        return null
      }
      let filteredManeuverReport = {}
      filteredManeuverReport.evasiveTargetToNominalRelativeDistanceResults =
        this.filterRelativeDistanceResult(
          maneuverReport.evasiveTargetToNominalRelativeDistanceResults,
          startDate,
          endDate
        )
      filteredManeuverReport.targetReactionManeuverReport =
        this.filterReactionManeuverReport(
          maneuverReport.targetReactionManeuverReport,
          startDate,
          endDate
        )
      filteredManeuverReport.chaserReactionManeuverReport =
        this.filterReactionManeuverReport(
          maneuverReport.chaserReactionManeuverReport,
          startDate,
          endDate
        )

      return { ...maneuverReport, ...filteredManeuverReport }
    },
    relativeDistanceComparatorRange(maneuvers, field) {
      let currentRange = null

      const context = 'ExtendedRelativeDistanceResults'

      const resultsWithoutManeuver =
        this.filteredReferenceChaserToTargetRelativeDistanceResults

      maneuvers.forEach((m, _) => {
        const resultsWithTargetReaction =
          m.targetReactionManeuverReport.relativeDistanceResults

        const resultsWithChaserReaction = this.displayChaserReaction
          ? m.chaserReactionManeuverReport.relativeDistanceResults
          : null

        const allResults = [
          resultsWithoutManeuver?.[field],
          resultsWithTargetReaction?.[field],
          resultsWithChaserReaction?.[field]
        ]
          .filter((fieldResults) => fieldResults != null)
          .flat()
          .map((x) => this.getDisplayValueFromPath(x, [field], context))

        const maneuverRange = this.getRange(allResults)
        currentRange = currentRange
          ? [
              Math.min(currentRange[0], maneuverRange[0]),
              Math.max(currentRange[1], maneuverRange[1])
            ]
          : maneuverRange
      })

      return currentRange
    },

    getRange(values) {
      let min = null,
        max = null,
        range = null

      values.forEach((val) => {
        if (min == null || val < min) {
          min = val
        }
        if (max == null || val > max) {
          max = val
        }
      })

      let increase = (max - min) / 3
      min -= increase
      max += increase

      if (min < 0 && min > -0.0001) {
        min = -0.0001
      }

      if (max > 0 && max < 0.0001) {
        max = 0.0001
      }

      range = [min, max]
      return range
    },
    removeManeuverFromComparator(maneuverToRemoveId) {
      this.$emit('remove-maneuver', maneuverToRemoveId)
      return maneuverToRemoveId
    },
    getDisplayValueFromPath(value, fieldPath, context) {
      const fieldType = dataTools.getFieldTypeFromPath(fieldPath, context, null)
      return dataTools.convertSi2gui(fieldType, value)
    },
    exportComparedManeuvers() {
      for (const maneuver of this.comparedManeuversReports) {
        this.$emit('export-maneuver', maneuver.id)
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');
</style>
